import { resetUserPassword } from "../api/adminUsers.js";
import { adminReactivateAccount, adminSuspendAccount, getLifecycle } from "../api/accountLifecycle.js";
import { setCredential } from "../db/credentials.js";
import {
  ACCOUNT_ROLES,
  ACCOUNT_STATUSES,
  PLANS,
  canManageAccounts,
  planLabel,
  roleLabel,
  statusLabel,
} from "./constants.js";
import { subscriptionFor } from "./store.js";
import { captureRestorePoint, RESTORE_SCOPES } from "./restorePoints.js";

export { ACCOUNT_ROLES, ACCOUNT_STATUSES, PLANS, planLabel, roleLabel, statusLabel };

export function filterAccounts(accounts, { role = "", status = "", planId = "", query = "" } = {}) {
  const q = String(query || "").trim().toLowerCase();
  return accounts.filter((row) => {
    if (role && row.role !== role) return false;
    if (status && row.status !== status) return false;
    if (planId) {
      const sub = subscriptionFor(row.id) || (row.organization_id ? subscriptionFor(row.organization_id) : null);
      if ((sub?.plan_id || "") !== planId) return false;
    }
    if (q) {
      const hay = `${row.name || ""} ${row.email || ""} ${row.phone || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return row.status !== "deleted";
  });
}

export function buildAdminOverview(accounts) {
  const visible = accounts.filter((row) => row.status !== "deleted");
  const byRole = Object.fromEntries(ACCOUNT_ROLES.map((row) => [row.id, 0]));
  const byStatus = Object.fromEntries(ACCOUNT_STATUSES.map((row) => [row.id, 0]));
  let mrr = 0;
  let suspended = 0;
  visible.forEach((row) => {
    byRole[row.role] = (byRole[row.role] || 0) + 1;
    byStatus[row.status] = (byStatus[row.status] || 0) + 1;
    if (row.status === "suspended") suspended += 1;
    const sub = subscriptionFor(row.id);
    if (sub?.status === "active" && sub.amount) mrr += Number(sub.amount || 0);
  });
  return {
    total: visible.length,
    byRole,
    byStatus,
    suspended,
    mrr,
  };
}

export function emergencyPasswordResetForAccount(session, account) {
  if (!canManageAccounts(session)) throw new Error("Admin desk only.");
  if (!account?.id) throw new Error("Account required.");
  const userId = account.journey_user_id || account.id;
  captureRestorePoint(userId, {
    label: "Before emergency password reset",
    actorId: session.userId,
    accountId: account.id,
    scope: RESTORE_SCOPES.account,
    source: "pre_change",
    force: true,
  });
  const result = resetUserPassword(userId, session.userId);
  try {
    setCredential(userId, result.temporary_password);
    const agents = JSON.parse(localStorage.getItem("dh_agents") || "[]");
    const index = agents.findIndex((row) => row.id === userId);
    if (index >= 0) {
      agents[index] = { ...agents[index], password: result.temporary_password, must_rotate: true };
      localStorage.setItem("dh_agents", JSON.stringify(agents));
    }
  } catch {
    /* optional legacy row */
  }
  return result;
}

export function adminLifecycleSummary(account) {
  const userId = account?.journey_user_id || account?.id;
  return getLifecycle(userId);
}

export function adminSuspendDesk(session, account, reason = "") {
  captureRestorePoint(account.journey_user_id || account.id, {
    label: "Before admin suspension",
    actorId: session.userId,
    accountId: account.id,
    scope: RESTORE_SCOPES.account,
    source: "pre_change",
    force: true,
  });
  return adminSuspendAccount(account.journey_user_id || account.id, session, { reason });
}

export function adminReactivateDesk(session, account, note = "") {
  captureRestorePoint(account.journey_user_id || account.id, {
    label: "Before admin reactivation",
    actorId: session.userId,
    accountId: account.id,
    scope: RESTORE_SCOPES.account,
    source: "pre_change",
    force: true,
  });
  return adminReactivateAccount(account.journey_user_id || account.id, session, { note });
}
